/**
 * Google reCAPTCHA v3 Utilities
 * 
 * Provides functions to load reCAPTCHA script and generate tokens
 * for bot protection. All verification happens server-side.
 */

import type { GRecaptcha } from '@/types/recaptcha.types';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
const RECAPTCHA_LOAD_TIMEOUT = 10000; // 10 seconds

/**
 * Load Google reCAPTCHA v3 script dynamically
 * 
 * @param siteKey - Public reCAPTCHA site key
 * @returns Promise that resolves when script is loaded
 * @throws Error if script fails to load within timeout
 */
export async function loadReCaptchaScript(siteKey: string): Promise<void> {
  // Check if script already exists
  if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
    // Wait for grecaptcha to be ready
    return waitForReCaptchaReady();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    // Set timeout for script loading
    const timeoutId = setTimeout(() => {
      reject(new Error('reCAPTCHA script load timeout'));
    }, RECAPTCHA_LOAD_TIMEOUT);

    script.onload = () => {
      clearTimeout(timeoutId);
      // Wait for grecaptcha.ready()
      waitForReCaptchaReady()
        .then(resolve)
        .catch(reject);
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Wait for grecaptcha object to be ready
 * 
 * @returns Promise that resolves when grecaptcha is ready
 */
function waitForReCaptchaReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(() => {
      if (window.grecaptcha) {
        clearInterval(checkInterval);
        window.grecaptcha.ready(() => {
          resolve();
        });
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.grecaptcha) {
        reject(new Error('grecaptcha object not available'));
      }
    }, 5000);
  });
}

/**
 * Generate reCAPTCHA token for a specific action
 * 
 * @param action - Action name (e.g., 'signup', 'login')
 * @param siteKey - Public reCAPTCHA site key
 * @returns Promise that resolves with token string
 * @throws Error if token generation fails or times out
 */
export async function getReCaptchaToken(
  action: string,
  siteKey: string
): Promise<string> {
  if (!window.grecaptcha) {
    throw new Error('reCAPTCHA not loaded. Call loadReCaptchaScript() first.');
  }

  try {
    // Generate token with timeout
    const tokenPromise = window.grecaptcha.execute(siteKey, { action });
    
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('reCAPTCHA token generation timeout'));
      }, 3000); // 3 second timeout
    });

    const token = await Promise.race([tokenPromise, timeoutPromise]);
    
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid reCAPTCHA token received');
    }

    return token;
  } catch (error) {
    console.error('reCAPTCHA token generation failed:', error);
    throw error;
  }
}

/**
 * Check if reCAPTCHA script is loaded and ready
 * 
 * @returns true if reCAPTCHA is ready, false otherwise
 */
export function isReCaptchaReady(): boolean {
  return !!window.grecaptcha;
}

/**
 * Remove reCAPTCHA script from DOM (useful for cleanup in tests)
 */
export function removeReCaptchaScript(): void {
  const script = document.getElementById(RECAPTCHA_SCRIPT_ID);
  if (script) {
    script.remove();
  }
  
  // Remove grecaptcha badge
  const badge = document.querySelector('.grecaptcha-badge');
  if (badge) {
    badge.remove();
  }

  // Clear grecaptcha from window
  if (window.grecaptcha) {
    delete window.grecaptcha;
  }
}
